import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import { Colxx } from "../../../components/common/CustomBootstrap";
import ActiveHelper from "../../../components/common/activeHelper";
import { firestore } from '../../../helpers/Firebase';
import Voucher from "./components/voucher";

const Vouchers = () => {
  const { user } = useSelector(state => state.authUser);
  const { customersVouchers } = useSelector(state => state.providers);
  const search = useSelector(state => state.settings.search) || '';
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(false);
  const [filteredVoucherList, setFilteredVoucherList] = useState([]);
  const [deleteVoucher, setDeleteVoucher] = useState(false);

  // Convert to MainPage tableColumns format
  const tableColumns = useMemo(() => [
    {
      Header: "Customer",
      accessor: "customer",
      type: 'label'
    },
    {
      Header: "Description",
      accessor: "description",
      type: 'label',
      formatValue: (value, original) => {
        const desc = value?.en || value || '--';
        return original?.promoCode ? `${desc} (${original.promoCode})` : desc;
      }
    },
    {
      Header: "Amount",
      accessor: "amount",
      type: 'amount'
    },
    {
      Header: "Created",
      accessor: "created",
      type: 'label',
      // Format date value for display
      formatValue: (value) => value ? moment(value).format('DD/MM/YYYY') : '--'
    },
    {
      Header: "Expiration Date",
      accessor: "exp",
      type: 'label',
      formatValue: (value) => value ? moment(value).format('DD/MM/YYYY') : '--'
    },
    {
      Header: "Redeemed",
      accessor: "redeemed",
      type: 'label',
      formatValue: (value) => value ? moment(value).format('DD/MM/YYYY') : '--'
    }
  ], []);

  const deleteVoucherHandler = useCallback((voucher) => {
    let ref = firestore().collection('users').doc(voucher?.userID).collection('walletLog').doc(voucher?.key)
    ref.delete().then(() => {
      setDeleteVoucher(false);
      setIsLoading(false);
    });
  }, []);

  // Filter vouchers based on search
  useEffect(() => {
    let vouchers = customersVouchers ? Object.values(customersVouchers) : [];

    // Apply search filter
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      vouchers = vouchers.filter(voucher => {
        return (
          (voucher?.customer?.toLowerCase().includes(searchLower)) ||
          (voucher?.description?.toLowerCase().includes(searchLower)) ||
          (voucher?.promoCode?.toLowerCase().includes(searchLower)) ||
          (voucher?.amount?.toString().includes(searchLower))
        );
      });
    }

    setFilteredVoucherList(vouchers);
  }, [customersVouchers, search]);

  const exportCSV = useCallback((data) => {
    if (!data || !data.length) return;

    // Define CSV headers
    const headers = [
      'Customer',
      'Description',
      'Amount',
      'Created',
      'Expiration Date',
      'Redeemed'
    ];

    // Convert voucher data to CSV format
    const voucherData = data.map(voucher => [
      voucher.customer,
      voucher.promoCode ? `${voucher.description} (${voucher.promoCode})` : voucher.description,
      Number(voucher.amount).toFixed(2),
      moment(voucher.created).format('DD/MM/YYYY'),
      moment(voucher.exp).format('DD/MM/YYYY'),
      voucher.redeemed ? moment(voucher.redeemed).format('DD/MM/YYYY') : '--'
    ]);

    // Combine headers and data
    const csvContent = [
      headers.join(','),
      ...voucherData.map(row => row.join(','))
    ].join('\n');

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vouchers_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Custom delete function to handle delete modal
  const handleDelete = useCallback((voucher) => {
    // Prevent deletion if voucher is redeemed
    if (voucher?.redeemed) {
      return;
    }
    setDeleteVoucher(voucher);
  }, []);

  // Custom edit function to handle edit modal
  const handleEdit = useCallback((voucher) => {
    setSelectedVoucher(voucher);
  }, []);

  // Custom create function to handle create modal
  const handleCreate = useCallback(() => {
    setSelectedVoucher({ addNew: true });
  }, []);

  return (
    <>
      {isLoading && (
        <div className="loading" />
      )}

      {selectedVoucher && (
        <Voucher
          selectedVoucher={selectedVoucher}
          dismiss={() => {
            setSelectedVoucher(false);
            setIsLoading(false);
          }}
        />
      )}

      <Modal backdrop="static" isOpen={!!deleteVoucher}>
        <ModalHeader>
          Delete Voucher
        </ModalHeader>
        <ModalBody>
          <Row>
            <Colxx xxs="12">
              <Label>Are you sure you want to delete this Voucher?</Label>
            </Colxx>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => {
            setDeleteVoucher(false);
          }}>Dismiss</Button>
          <Button color='danger' onClick={() => {
            setIsLoading(true);
            deleteVoucherHandler(deleteVoucher);
          }}>Delete Voucher</Button>
        </ModalFooter>
      </Modal>

      <ActiveHelper
        title="Vouchers / Compensations"
        selected="vouchers"
        data={filteredVoucherList}
        tableColumns={tableColumns}
        permenatDelete={true}
        exportCSV={exportCSV}
        defaultSorting="created"
        sortDirection="desc"
        renderAsModal={true}
        modalSize="lg"
        modalBackdrop="static"
        hideDefaultDeleteButton={false}
        customDeleteFunction={handleDelete}
        customDeleteTitle="Delete"
        customEditFunction={handleEdit}
        customCreateFunction={handleCreate}
        showCreateButton={true}
        user={user}
      />
    </>
  );
};



export default Vouchers;
